<template>
  <dx-modal :value="true" @input="close"
            title="Conciliar lançamento"
            id="bank-reconcile-modal" size="lg">
    <div class="statement-line mb-2">
      <div>
        <span class="text-gray">{{ statement.date | date('date', true) }}</span>
        <strong class="ml-2">{{ statement.description }}</strong>
      </div>
      <strong :class="statement.signedValue < 0 ? 'text-error' : 'text-success'">
        {{ statement.signedValue | currency }}
      </strong>
    </div>

    <ul class="tab tab-block">
      <li class="tab-item" :class="{active: mode === 'match'}">
        <a href="javascript:void(0)" @click="mode = 'match'">Conciliar com lançamento</a>
      </li>
      <li class="tab-item" :class="{active: mode === 'create'}">
        <a href="javascript:void(0)" @click="mode = 'create'">Criar lançamento</a>
      </li>
    </ul>

    <template v-if="mode === 'match'">
      <div class="loading loading-lg" v-if="loading" />
      <div class="empty" v-else-if="candidates.length === 0">
        <p class="empty-title h6">Nenhum lançamento compatível</p>
        <p class="empty-subtitle">
          Não há baixa desta conta, do mesmo tipo, em um período próximo.
          Use "Criar lançamento" para registrar este valor.
        </p>
      </div>
      <template v-else>
        <p class="text-gray mb-1">
          Selecione um ou mais lançamentos até somar o valor do extrato.
        </p>
        <table class="table table-hover">
          <thead>
          <tr>
            <th style="width: 40px"></th>
            <th style="width: 90px">Data</th>
            <th>Pagador / Favorecido</th>
            <th>Categoria</th>
            <th class="text-right" style="width: 130px">Valor</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in candidates" :key="`${item.id}-${item.paymentIndex}`"
              class="c-hand" @click="item.selected = !item.selected">
            <td>
              <label class="form-checkbox" @click.stop>
                <input type="checkbox" v-model="item.selected">
                <i class="form-icon"/>
              </label>
            </td>
            <td>{{ item.date | date('date', true) }}</td>
            <td>{{ item.entity ? item.entity.name : '-' }}</td>
            <td>{{ item.category ? item.category.name : '-' }}</td>
            <td class="text-right" :class="valueMatches(item) ? 'text-success text-bold' : ''">
              {{ item.value | currency }}
            </td>
          </tr>
          </tbody>
        </table>
        <div class="reconcile-total" v-if="selected.length > 0">
          <span>
            <strong>{{ selected.length }}</strong>
            {{ selected.length === 1 ? 'lançamento selecionado' : 'lançamentos selecionados' }}
          </span>
          <span>
            Somam <strong>{{ selectedTotal | currency }}</strong>
            de <strong>{{ statementValue | currency }}</strong>
          </span>
          <span>
            Diferença:
            <strong :class="balanced ? 'text-success' : 'text-error'">
              {{ difference | currency }}
            </strong>
          </span>
        </div>
        <div class="toast toast-error mt-2" v-if="selected.length > 0 && !balanced">
          <template v-if="difference > 0">
            Faltam <strong>{{ difference | currency }}</strong> para fechar o valor do extrato.
            Selecione outro lançamento ou use "Criar lançamento"
            para registrar a diferença.
          </template>
          <template v-else>
            A seleção passa <strong>{{ -difference | currency }}</strong>
            do valor do extrato. Desmarque algum lançamento.
          </template>
        </div>
      </template>
    </template>

    <template v-else>
      <div class="columns">
        <div class="column col-12 form-group"
             :class="{'has-error': $v.form.entity.id.$error}">
          <label class="form-label">Pagador / Favorecido *</label>
          <dx-autocomplete
            v-model="entity"
            :source="findEntity"
            label="name"
            track-by="id"
            @select="setEntity"
            :debounce="800"
            placeholder="Nome do favorecido...">
            <button slot="action" @click="unsetEntity"
                    class="btn btn-action input-group-btn btn-icon"
                    :class="entity ? 'btn-gray' : 'btn-neutral'"
                    tabindex="-1">
              <fa-icon :icon="['fal', entity ? 'times' : 'search']"></fa-icon>
            </button>
            <template v-slot="{ item }">
              <div>{{ item.name }}</div>
            </template>
          </dx-autocomplete>
          <template v-if="$v.form.entity.id.$error">
            <div class="form-input-hint">Selecione o pagador ou favorecido</div>
          </template>
        </div>
        <div class="column col-6 col-sm-12 form-group"
             :class="{'has-error': $v.form.categoryId.$error}">
          <label class="form-label">Categoria *</label>
          <select class="form-select" v-model="form.categoryId"
                  @blur="$v.form.categoryId.$touch()">
            <option value="">[Selecione]</option>
            <option v-for="item in categories" :value="item.id" :key="item.id">
              {{ item.name }}
            </option>
          </select>
          <template v-if="$v.form.categoryId.$error">
            <div class="form-input-hint">Selecione a categoria</div>
          </template>
        </div>
        <div class="column col-6 col-sm-12 form-group"
             :class="{'has-error': $v.form.methodId.$error}">
          <label class="form-label">Forma de pagamento *</label>
          <select class="form-select" v-model="form.methodId"
                  @blur="$v.form.methodId.$touch()">
            <option value="">[Selecione]</option>
            <option v-for="item in methods" :value="item.id" :key="item.id">
              {{ item.name }}
            </option>
          </select>
          <template v-if="$v.form.methodId.$error">
            <div class="form-input-hint">Selecione a forma de pagamento</div>
          </template>
        </div>
        <div class="column col-6 col-sm-12 form-group">
          <label class="form-label">Centro de custo</label>
          <select class="form-select" v-model="form.costCenterId">
            <option value="">[Nenhum]</option>
            <option v-for="item in costCenters" :value="item.id" :key="item.id">
              {{ item.name }}
            </option>
          </select>
        </div>
        <div class="column col-12 form-group">
          <label class="form-label">Observação</label>
          <input type="text" class="form-input" v-model="form.notes"
                 maxlength="200" :placeholder="statement.description">
        </div>
      </div>
      <div class="toast mt-2">
        Será criado um lançamento de
        <strong>{{ createdType === 'expense' ? 'despesa' : 'receita' }}</strong>
        no valor de <strong>{{ statementValue | currency }}</strong>,
        já baixado em {{ statement.date | date('date', true) }}, e conciliado com esta linha.
      </div>
    </template>

    <template slot="footer">
      <button class="btn mr-1" @click="close">Cancelar</button>
      <button class="btn btn-primary"
              :disabled="!canSave || saving"
              :class="{loading: saving}"
              @click="save">Conciliar</button>
    </template>
  </dx-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

const toCents = value => Math.round(Number(value || 0) * 100);

export default {
  props: {
    statement: {
      type: Object,
      required: true,
    },
    initialMode: {
      type: String,
      default: 'match',
    },
  },
  data() {
    return {
      mode: this.initialMode,
      loading: false,
      saving: false,
      candidates: [],
      categories: [],
      costCenters: [],
      methods: [],
      entity: null,
      form: {
        entity: { id: '', name: '' },
        categoryId: '',
        costCenterId: '',
        methodId: '',
        notes: '',
      },
    };
  },
  computed: {
    createdType() {
      return this.statement.signedValue < 0 ? 'expense' : 'revenue';
    },
    statementValue() {
      return Math.abs(this.statement.signedValue);
    },
    statementCents() {
      return toCents(this.statementValue);
    },
    selected() {
      return this.candidates.filter(item => item.selected);
    },
    selectedCents() {
      return this.selected.reduce((sum, item) => sum + toCents(item.value), 0);
    },
    selectedTotal() {
      return this.selectedCents / 100;
    },
    difference() {
      return (this.statementCents - this.selectedCents) / 100;
    },
    balanced() {
      return this.selected.length > 0 && this.statementCents === this.selectedCents;
    },
    canSave() {
      return this.mode === 'match' ? this.balanced : true;
    },
  },
  validations: {
    form: {
      // entidade é obrigatória como no formulário normal: sem entity._id o
      // lançamento é excluído de ReportService.financialTransactionPrint e não
      // aparece no filtro Pagador/Favorecido.
      entity: { id: { required } },
      categoryId: { required },
      methodId: { required },
    },
  },
  mounted() {
    this.loadCandidates();
    this.loadSupportData();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    /**
     * A linha do banco traz o sinal; o lançamento traz o valor absoluto.
     * Comparar em centavos evita o clássico erro de ponto flutuante.
     */
    valueMatches(item) {
      return toCents(item.value) === this.statementCents;
    },
    findEntity(search) {
      const params = { search: `^${search}`, type: this.createdType };
      return this.$http.get('/financial-transactions/entities', { params })
        .then(({ data }) => data.items)
        .catch(() => []);
    },
    setEntity(data) {
      this.form.entity = data || { id: '', name: '' };
    },
    unsetEntity() {
      this.setEntity(null);
      this.entity = null;
    },
    loadCandidates() {
      this.loading = true;
      return this.$http
        .get(`/bank-statements/${this.statement.id}/candidates`)
        .then(({ data }) => {
          this.candidates = data.items.map(item => ({ ...item, selected: false }));
        })
        .catch(() => {
          this.$toast.error('Não foi possível carregar os lançamentos compatíveis.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadSupportData() {
      const params = { limit: 0, active: true };

      this.$http.get('/categories', { params: { ...params, type: this.createdType } })
        .then(({ data }) => { this.categories = data.items; })
        .catch(() => {});
      this.$http.get('/cost-centers', { params })
        .then(({ data }) => { this.costCenters = data.items; })
        .catch(() => {});
      this.$http.get('/payment-methods', { params })
        .then(({ data }) => { this.methods = data.items; })
        .catch(() => {});
    },
    buildPayload() {
      if (this.mode === 'match') {
        return {
          statementIds: [this.statement.id],
          transactions: this.selected.map(({ id, paymentIndex }) => ({ id, paymentIndex })),
        };
      }

      return {
        statementIds: [this.statement.id],
        transactions: [],
        adjustment: {
          value: this.statement.signedValue,
          kind: 'other',
          entityId: this.form.entity.id,
          entityName: this.form.entity.name,
          categoryId: this.form.categoryId,
          costCenterId: this.form.costCenterId || null,
          methodId: this.form.methodId,
          notes: this.form.notes || null,
        },
      };
    },
    save() {
      if (this.mode === 'create') {
        this.$v.form.$touch();
        if (this.$v.form.$error) {
          return;
        }
      }

      this.saving = true;

      this.$http.post('/bank-reconciliations', this.buildPayload())
        .then(() => {
          this.$toast.show('Lançamento conciliado com sucesso!');
          // o pai atualiza a linha localmente, sem recarregar a lista
          this.$emit('saved', this.statement.id);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

#bank-reconcile-modal {
  .statement-line {
    align-items: center;
    background-color: lighten($gray-color-light, 5%);
    border-radius: $border-radius;
    display: flex;
    justify-content: space-between;
    padding: $layout-spacing;
  }
  .reconcile-total {
    align-items: baseline;
    background-color: lighten($gray-color-light, 5%);
    border-radius: $border-radius;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-lg;
    justify-content: space-between;
    margin-top: $layout-spacing;
    padding: $layout-spacing;
  }
}
</style>
